/* eslint-disable no-underscore-dangle, import/no-unresolved */
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Box, CircularProgress } from '@mui/material';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';

const EMOTION_MAP = {
  happy: { label: '행복', color: '#10b981' },
  neutral: { label: '중립', color: '#94a3b8' },
  sad: { label: '슬픔', color: '#3b82f6' },
  angry: { label: '화남', color: '#ef4444' },
  anger: { label: '화남', color: '#ef4444' },
  surprised: { label: '놀람', color: '#f59e0b' },
  surprise: { label: '놀람', color: '#f59e0b' },
  fear: { label: '두려움', color: '#8b5cf6' },
  fearful: { label: '두려움', color: '#8b5cf6' },
  disgust: { label: '혐오', color: '#6b7280' },
};

const FALLBACK_COLORS = ['#0ea5e9', '#f97316', '#a855f7', '#14b8a6', '#ec4899'];

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  const pct = d.__total > 0 ? Math.round((d.count / d.__total) * 100) : 0;
  return (
    <div
      style={{
        background: 'white',
        border: '1px solid #e2e8f0',
        borderRadius: 8,
        padding: '8px 12px',
        fontSize: 12,
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      }}
    >
      <p style={{ fontWeight: 'bold', margin: 0 }}>{d.label}</p>
      <p style={{ margin: 0 }}>
        {d.count}회 ({pct}%)
      </p>
    </div>
  );
};

const CustomLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  count,
  __total,
}) => {
  const pct = __total > 0 ? Math.round((count / __total) * 100) : 0;
  if (pct < 8) return null;
  const rad = (Math.PI / 180) * midAngle;
  const r = innerRadius + (outerRadius - innerRadius) * 0.6;
  return (
    <text
      x={cx + r * Math.cos(-rad)}
      y={cy + r * Math.sin(-rad)}
      textAnchor="middle"
      dominantBaseline="central"
      style={{ fontSize: 10, fontWeight: 'bold', fill: 'white' }}
    >
      {pct}%
    </text>
  );
};

const PiCarePieChartEmotion = () => {
  const { emotionSummary, loading } = useSelector(
    (state) => state.picareDashboard,
  );

  const data = useMemo(() => {
    if (!emotionSummary?.length) return [];
    const total = emotionSummary.reduce((s, r) => s + r.count, 0);
    return emotionSummary.map((item, i) => {
      const meta = EMOTION_MAP[item._id?.toLowerCase()];
      return {
        emotion: item._id,
        label: meta?.label ?? item._id,
        count: item.count,
        color: meta?.color ?? FALLBACK_COLORS[i % FALLBACK_COLORS.length],
        __total: total,
      };
    });
  }, [emotionSummary]);

  const total = data.reduce((s, d) => s + d.count, 0);

  if (loading.emotionSummary) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <CircularProgress color="secondary" />
      </Box>
    );
  }

  if (!data.length) {
    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#cbd5e1',
          fontSize: 14,
          gap: 4,
        }}
      >
        <span>데이터 없음</span>
        <span style={{ fontSize: 12 }}>감정 데이터가 쌓이면 표시됩니다</span>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', gap: 4 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
            <Pie
              data={data}
              dataKey="count"
              innerRadius="40%"
              outerRadius="85%"
              labelLine={false}
              label={<CustomLabel />}
            >
              {data.map((entry) => (
                <Cell key={entry.emotion} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: 5,
          paddingRight: 4,
          fontSize: 12,
          minWidth: 60,
        }}
      >
        <p style={{ color: '#94a3b8', margin: '0 0 2px', fontSize: 12 }}>
          총 {total}회
        </p>
        {data.map((d) => (
          <div
            key={d.emotion}
            style={{ display: 'flex', alignItems: 'center', gap: 4 }}
          >
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: 2,
                flexShrink: 0,
                background: d.color,
              }}
            />
            <span
              style={{
                color: '#64748b',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {d.label}
            </span>
            <span style={{ color: '#94a3b8', marginLeft: 'auto' }}>
              {d.count}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PiCarePieChartEmotion;
